import { useState, useEffect, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BookOpenCheck, Upload, FileText, Check, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SectionDetail } from "@/components/theory/TheoryCourses";
import VfrgReader from "@/components/theory/VfrgReader";

const SWATCHES = ["#0E9F8E", "#2563EB", "#EA7317", "#7C3AED", "#DC2626", "#059669", "#D946EF", "#0891B2", "#CA8A04", "#475569"];
const token = () => localStorage.getItem("session_token") || "";

export default function VfrgCourses({ view = "grid" }) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reading, setReading] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/theory/sections");
      const vfrg = (r.data || []).filter((s) => s.group === "vfrg").sort((a, b) => (a.seq || 0) - (b.seq || 0));
      setChapters(vfrg);
      setSelected((prev) => (prev ? (vfrg.find((c) => c.id === prev.id) || prev) : prev));
    } catch { toast.error("Failed to load VFRG"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (selected) return (
    <>
      <SectionDetail section={selected} backLabel="All VFRG chapters"
        onBack={() => { setSelected(null); load(); }} onEditSection={() => setEditing(selected)} />
      {editing && <ChapterEditor chapter={editing} onClose={() => setEditing(null)} onSaved={(s) => { setEditing(null); setSelected(s); load(); }} />}
    </>
  );

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-5" data-testid="vfrg-courses">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-3">
        <BookOpenCheck className="h-6 w-6 text-lime-500 shrink-0" />
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-900">Visual Flight Rules Guide</h2>
          <p className="text-sm text-slate-500">Browse the VFRG by chapter → subject → sub-subject, with study notes and quizzes.</p>
        </div>
      </div>

      <div className={view === "list" ? "bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden" : "grid sm:grid-cols-2 gap-4"} data-testid="vfrg-chapters">
        {view === "list" ? chapters.map((c) => (
          <div key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`vfrg-chapter-${c.id}`}>
            <button onClick={() => setSelected(c)} className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-heading font-bold shrink-0" style={{ backgroundColor: c.colour || "#0f172a" }} data-testid={`vfrg-open-${c.id}`}>
              {(c.title.match(/\d+/) || ["•"])[0]}
            </button>
            <button onClick={() => setSelected(c)} className="min-w-0 flex-1 text-left">
              <p className="font-heading font-bold text-slate-900 truncate">{c.title}</p>
              <p className="text-xs text-slate-500 truncate">{c.lesson_count ?? 0} topics{c.reference_pdf ? " · PDF" : ""}</p>
            </button>
            <Button size="sm" className="h-8 text-xs text-white font-semibold shrink-0" style={{ backgroundColor: c.colour || "#0f172a" }} onClick={() => setReading(c)} data-testid={`vfrg-read-${c.id}`}>
              <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Read
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 shrink-0" onClick={() => setEditing(c)} data-testid={`vfrg-edit-${c.id}`}>Edit</Button>
          </div>
        )) : chapters.map((c) => (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-all group" data-testid={`vfrg-chapter-${c.id}`}>
            <div className="h-2" style={{ backgroundColor: c.colour || "#0f172a" }} />
            <div className="p-5 flex items-start gap-4">
              <button onClick={() => setSelected(c)} className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-heading font-bold text-lg shrink-0" style={{ backgroundColor: c.colour || "#0f172a" }} data-testid={`vfrg-open-${c.id}`}>
                {(c.title.match(/\d+/) || ["•"])[0]}
              </button>
              <button onClick={() => setSelected(c)} className="min-w-0 flex-1 text-left">
                <h3 className="font-heading font-bold text-slate-900 leading-snug">{c.title}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</p>
                <div className="text-xs text-slate-400 mt-2 flex items-center gap-2">
                  <span>{c.lesson_count ?? 0} topics</span>
                  {c.reference_pdf && <span className="inline-flex items-center gap-1 text-slate-500"><FileText className="h-3 w-3" /> PDF</span>}
                </div>
              </button>
              <div className="flex flex-col gap-2 items-end shrink-0">
                <Button size="sm" className="h-8 text-xs text-white font-semibold" style={{ backgroundColor: c.colour || "#0f172a" }} onClick={() => setReading(c)} data-testid={`vfrg-read-${c.id}`}>
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Read
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => setEditing(c)} data-testid={`vfrg-edit-${c.id}`}>Edit</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {chapters.length === 0 && <p className="text-sm text-slate-400 text-center py-16">VFRG chapters aren't loaded yet.</p>}

      {reading && <VfrgReader chapter={reading} onClose={() => { setReading(null); load(); }} />}
      {editing && <ChapterEditor chapter={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ChapterEditor({ chapter, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...chapter }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadPdf = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Please choose a PDF file"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd);
      set("reference_pdf", r.data.path);
      toast.success("Reference PDF uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      title: f.title, description: f.description, disciplines: f.disciplines || [], levels: f.levels || [],
      seq: f.seq || 0, colour: f.colour || "", group: f.group || "vfrg", reference_pdf: f.reference_pdf || "",
    };
    try {
      const r = await api.put(`/theory/sections/${f.id}`, payload);
      toast.success("Chapter saved"); onSaved(r.data);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const pdfUrl = f.reference_pdf ? `${API}/files/${f.reference_pdf}?auth=${encodeURIComponent(token())}` : null;

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vfrg-chapter-editor">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit chapter</DialogTitle>
          <DialogDescription>Update the title, colour and reference PDF for this VFRG chapter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Title</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="vfrg-title" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
            <Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1.5" data-testid="vfrg-desc" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Chapter colour</Label>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {SWATCHES.map((c) => (
                <button key={c} onClick={() => set("colour", c)} data-testid={`vfrg-swatch-${c.replace('#','')}`}
                  className="h-8 w-8 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110"
                  style={{ backgroundColor: c, borderColor: f.colour === c ? "#0f172a" : "transparent" }}>
                  {f.colour === c && <Check className="h-4 w-4 text-white" />}
                </button>
              ))}
              <label className="h-8 w-8 rounded-full border border-slate-200 overflow-hidden cursor-pointer relative" title="Custom colour">
                <input type="color" value={f.colour || "#0f172a"} onChange={(e) => set("colour", e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="vfrg-colour-picker" />
                <span className="block h-full w-full" style={{ background: "conic-gradient(red,orange,yellow,green,blue,violet,red)" }} />
              </label>
              <span className="text-xs text-slate-400 font-mono">{f.colour || "—"}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reference PDF (for students)</Label>
            <div className="mt-1.5 flex items-center gap-3 rounded-lg border border-slate-200 p-3">
              <FileText className={`h-8 w-8 shrink-0 ${f.reference_pdf ? "text-lime-500" : "text-slate-300"}`} />
              <div className="flex-1 min-w-0">
                <input type="file" accept="application/pdf" id="vfrg-pdf-input" className="hidden" data-testid="vfrg-pdf-input" onChange={(e) => uploadPdf(e.target.files?.[0])} />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button type="button" size="sm" variant="outline" onClick={() => document.getElementById("vfrg-pdf-input").click()} disabled={uploading} data-testid="vfrg-pdf-upload">
                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} {f.reference_pdf ? "Replace PDF" : "Upload PDF"}
                  </Button>
                  {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-sm text-sky-600 hover:underline" data-testid="vfrg-pdf-view">View</a>}
                  {f.reference_pdf && <button className="text-xs text-slate-400 hover:text-red-500" onClick={() => set("reference_pdf", "")}>Remove</button>}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Students can open this chapter PDF for reference.</p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="vfrg-save">{saving ? "Saving…" : "Save chapter"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
